import {
  createInsertPackages,
  downloadInsertPackage,
  type ApiContext,
  type InsertPackage,
  type SlideResult,
} from "./apiClient";

type InsertOptions = {
  context: ApiContext;
  keepFormatting: boolean;
};

type InsertionAnchor = {
  targetSlideId?: string;
};

type OfficeLikeError = Error & {
  code?: string;
  debugInfo?: unknown;
  traceMessages?: unknown;
};

type SlideDebugInfo = {
  contentUuid?: string;
  fileUuid: string;
  id: string;
  slideNumber?: number;
  sourceFileName?: string;
  sourceKind: SlideResult["sourceKind"];
  templateKind?: SlideResult["templateKind"];
  templateUuid?: string;
  title: string;
};

function isPowerPointApiAvailable() {
  return (
    typeof PowerPoint !== "undefined" && typeof PowerPoint.run === "function"
  );
}

function isInsertSlidesApiSupported() {
  return (
    typeof Office === "undefined" ||
    Office.context?.requirements?.isSetSupported("PowerPointApi", "1.2") !==
      false
  );
}

function enableExtendedOfficeErrorLogging() {
  if (typeof OfficeExtension !== "undefined") {
    OfficeExtension.config.extendedErrorLogging = true;
  }
}

function summarizeDebugValue(value: unknown): unknown {
  if (typeof value === "string") {
    return value.length > 300 ? `[string length ${value.length}]` : value;
  }

  if (Array.isArray(value)) {
    return {
      count: value.length,
      items: value.slice(0, 10).map(summarizeDebugValue),
    };
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(
        ([key, nextValue]) => [key, summarizeDebugValue(nextValue)],
      ),
    );
  }

  return value;
}

function safeStringify(value: unknown) {
  const seen = new WeakSet<object>();

  return JSON.stringify(
    value,
    (_key, nextValue) => {
      if (nextValue instanceof Error) {
        return {
          message: nextValue.message,
          name: nextValue.name,
          stack: nextValue.stack,
        };
      }

      if (nextValue && typeof nextValue === "object") {
        if (seen.has(nextValue)) {
          return "[Circular]";
        }

        seen.add(nextValue);
      }

      return nextValue;
    },
    2,
  );
}

function describeOfficeError(error: unknown) {
  const candidate = error as OfficeLikeError | null;
  return {
    code: candidate?.code,
    debugInfo: summarizeDebugValue(candidate?.debugInfo),
    message: candidate?.message,
    name: candidate?.name,
    stack: candidate?.stack,
    traceMessages: summarizeDebugValue(candidate?.traceMessages),
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return window.btoa(binary);
}

function describePptxBuffer(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  return {
    isZipPackage: bytes[0] === 0x50 && bytes[1] === 0x4b,
    signature: Array.from(bytes.slice(0, 8))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join(" "),
  };
}

function summarizeSlide(slide: SlideResult): SlideDebugInfo {
  return {
    contentUuid: slide.contentUuid,
    fileUuid: slide.fileUuid,
    id: slide.id,
    slideNumber: slide.slideNumber,
    sourceFileName: slide.sourceFileName,
    sourceKind: slide.sourceKind,
    templateKind: slide.templateKind,
    templateUuid: slide.templateUuid,
    title: slide.title,
  };
}

async function getBeforeCurrentSlideAnchor(): Promise<InsertionAnchor> {
  if (!isPowerPointApiAvailable()) {
    throw new Error("Open this add-in in PowerPoint to insert slides.");
  }

  return PowerPoint.run(async (context) => {
    const selectedSlides = context.presentation.getSelectedSlides();
    const slides = context.presentation.slides;

    selectedSlides.load("items/id");
    slides.load("items/id");
    await context.sync();

    const activeSlideId = selectedSlides.items[0]?.id;
    if (!activeSlideId) {
      throw new Error("Could not determine the current slide.");
    }

    const activeSlideIndex = slides.items.findIndex(
      (slide) => slide.id === activeSlideId,
    );
    if (activeSlideIndex < 0) {
      throw new Error("Could not locate the current slide.");
    }

    if (activeSlideIndex === 0) {
      return {};
    }

    return {
      targetSlideId: slides.items[activeSlideIndex - 1].id,
    };
  });
}

async function insertPackage(
  packageItem: InsertPackage,
  base64: string,
  anchor: InsertionAnchor,
  sourceSlide?: SlideDebugInfo,
) {
  const insertOptions: PowerPoint.InsertSlideOptions = {
    formatting: packageItem.formatting,
    ...anchor,
  };

  try {
    await PowerPoint.run(async (context) => {
      context.presentation.insertSlidesFromBase64(base64, insertOptions);

      await context.sync();
    });
  } catch (error) {
    const details = {
      error: describeOfficeError(error),
      insertOptions,
      packageUuid: packageItem.packageUuid,
      rawPptxBytes: packageItem.rawPptxBytes,
      sourceSlide,
    };
    console.error("PowerPoint insertSlidesFromBase64 failed", details);
    console.error(
      "PowerPoint insertSlidesFromBase64 failed details",
      safeStringify(details),
    );
    throw error;
  }
}

function logInsertPackagePrepared(
  packageItem: InsertPackage,
  buffer: ArrayBuffer,
  base64: string,
  anchor: InsertionAnchor,
  sourceSlide?: SlideDebugInfo,
) {
  const bufferDescription = describePptxBuffer(buffer);
  const details = {
    anchor,
    base64Length: base64.length,
    buffer: bufferDescription,
    downloadedBytes: buffer.byteLength,
    formatting: packageItem.formatting,
    packageUuid: packageItem.packageUuid,
    rawPptxBytes: packageItem.rawPptxBytes,
    sourceSlide,
  };

  console.info("PowerPoint insert package prepared", details);
  console.info(
    "PowerPoint insert package prepared details",
    safeStringify(details),
  );

  if (buffer.byteLength !== packageItem.rawPptxBytes) {
    console.warn("Insert package byte size differs from backend metadata", {
      downloadedBytes: buffer.byteLength,
      formatting: packageItem.formatting,
      packageUuid: packageItem.packageUuid,
      rawPptxBytes: packageItem.rawPptxBytes,
      sourceSlide,
    });
  }
}

export const powerPointService = {
  async insertSlides(slides: SlideResult[], options: InsertOptions) {
    if (slides.length === 0) {
      return;
    }

    enableExtendedOfficeErrorLogging();

    if (!isInsertSlidesApiSupported()) {
      throw new Error(
        "This PowerPoint client does not support slide insertion from Base64.",
      );
    }

    const selectedSlides = slides.map(summarizeSlide);
    console.info("PowerPoint insert request prepared", {
      keepFormatting: options.keepFormatting,
      slides: selectedSlides,
    });
    console.info(
      "PowerPoint insert request prepared details",
      safeStringify({
        keepFormatting: options.keepFormatting,
        slides: selectedSlides,
      }),
    );

    const anchor = await getBeforeCurrentSlideAnchor();
    const packages = await createInsertPackages(
      options.context,
      slides,
      options.keepFormatting,
    );
    if (!packages.length) {
      throw new Error("No insertion package was returned.");
    }

    const packagesInInsertOrder = packages
      .map((packageItem, index) => ({
        packageItem,
        sourceSlide: selectedSlides[index],
      }))
      .reverse();

    for (const { packageItem, sourceSlide } of packagesInInsertOrder) {
      const buffer = await downloadInsertPackage(
        options.context,
        packageItem.downloadUrl,
      );
      const base64 = arrayBufferToBase64(buffer);
      logInsertPackagePrepared(
        packageItem,
        buffer,
        base64,
        anchor,
        sourceSlide,
      );
      await insertPackage(packageItem, base64, anchor, sourceSlide);
    }
  },
};
